import { computed, inject, Injectable, resource, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TokenSetting } from '../../models/token-setting.model';
import { SettingsService } from '../settings-page/settings-service/settings.service';
import {
  AllocationTypeKey,
  TokenAllocation,
} from '../../models/token-allocation.model';
import { TokenTypeKey } from '../../models/token-setting.model';
import { PaginatedTokenUseEventsResponse } from '../../models/token-use-event.model';

export type CreateTokenAllocation = {
  token_type: TokenTypeKey;
  allocated_amount: number;
  valid_from: string;
  valid_to: string;
  allocation_type:
    AllocationTypeKey.COMPLIMENTARY | AllocationTypeKey.CUSTOMER_PURCHASE;
};

@Injectable({
  providedIn: 'root',
})
export class BillingService {
  http = inject(HttpClient);
  private settingsService = inject(SettingsService);
  private settings = toSignal(
    this.settingsService.settings$.pipe(
      filter((settings) => settings !== null),
    ),
  );

  private tokenBillingEnabledInSettings = computed(
    () => this.settings()?.token_billing_enabled ?? false,
  );

  tokenSettings = resource({
    params: () =>
      this.tokenBillingEnabledInSettings() ? ({} as const) : undefined,

    loader: () => this.fetchTokenSettings(),
  });

  private async fetchTokenSettings(): Promise<TokenSetting[]> {
    return firstValueFrom(
      this.http.get<TokenSetting[]>('/billing-api/token-settings/'),
    );
  }

  tokenBillingEnabled = computed(() => {
    if (!this.tokenBillingEnabledInSettings()) {
      return false;
    }
    if (this.tokenSettings.hasValue()) {
      return this.tokenSettings.value().some((ts) => ts.enabled);
    }
    return false;
  });

  patchTokenSetting(
    id: number,
    data: Pick<
      TokenSetting,
      'enabled' | 'monthly_allocation' | 'on_demand_price_per_token'
    >,
    options?: { adjustCurrent?: boolean },
  ): Promise<TokenSetting> {
    let params = new HttpParams();
    if (options?.adjustCurrent) {
      params = params.set('adjust_current', 'true');
    }
    return firstValueFrom(
      this.http.patch<TokenSetting>(
        `/billing-api/token-settings/${id}/`,
        data,
        { params },
      ),
    );
  }

  tokenAllocations = resource({
    params: () =>
      this.tokenBillingEnabledInSettings() ? ({} as const) : undefined,

    loader: () => this.fetchTokenAllocations(),
  });

  private async fetchTokenAllocations(): Promise<TokenAllocation[]> {
    return firstValueFrom(
      this.http.get<TokenAllocation[]>(
        '/billing-api/token-allocations/?valid_now=true',
      ),
    );
  }

  createTokenAllocation(data: CreateTokenAllocation): Promise<TokenAllocation> {
    return firstValueFrom(
      this.http.post<TokenAllocation>('/billing-api/token-allocations/', data),
    );
  }

  private tokenUseEventsParams = signal({ limit: 20, offset: 0 });

  tokenUseEvents = resource({
    params: () =>
      this.tokenBillingEnabledInSettings()
        ? this.tokenUseEventsParams()
        : undefined,

    loader: ({ params }) =>
      this.fetchTokenUseEvents(params.limit, params.offset),
  });

  setTokenUseEventsPage(limit: number, offset: number): void {
    this.tokenUseEventsParams.set({ limit, offset });
  }

  private async fetchTokenUseEvents(
    limit: number,
    offset: number,
  ): Promise<PaginatedTokenUseEventsResponse> {
    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    return firstValueFrom(
      this.http.get<PaginatedTokenUseEventsResponse>(
        '/billing-api/token-use-events/',
        { params },
      ),
    );
  }

  async refundTokenUseEvent(eventId: number): Promise<void> {
    await firstValueFrom(
      this.http.post(`/billing-api/token-use-events/${eventId}/refund/`, {}),
    );
    this.tokenUseEvents.reload();
    this.tokenAllocations.reload();
  }
}
